/**
 * CreateFromTtsDialog — type a script, preview the synthesized audio, save
 * it as a recording in the library.
 *
 * Uses POST /api/recordings/tts/preview for "hear it first" (returns raw
 * mp3 bytes — no row persisted) and POST /api/recordings/from-tts to
 * commit. Server picks the Polly voice per language when the optional
 * `voice_id` field is left blank.
 *
 * Voice choices surfaced here are the subset Polly's neural engine
 * supports for our three portal languages — they match the defaults in
 * backend/src/tts/synthesizer.ts VOICE_MAP. Additional voices can be
 * added without a schema change (the endpoint validates `voice_id` only
 * by length, not by an enum).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recordings, type RecordingRow } from '@/api/recordings';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Loader2, Sparkles } from 'lucide-react';

// Voices per language. `default` means "let the server pick" (resolved from
// the VOICE_MAP on the backend). Keep the list small; the UI is a simple
// select, not an exhaustive Polly voice browser.
const VOICES_BY_LANG: Record<'en' | 'he' | 'yi', Array<{ id: string; label: string }>> = {
  en: [
    { id: 'default', label: 'Default (Matthew, male)' },
    { id: 'Matthew', label: 'Matthew (English, US, male)' },
    { id: 'Joanna', label: 'Joanna (English, US, female)' },
    { id: 'Amy', label: 'Amy (English, UK, female)' },
  ],
  he: [
    { id: 'default', label: 'Default (Suri — only Hebrew voice available)' },
    { id: 'Suri', label: 'Suri (Hebrew, female)' },
  ],
  yi: [
    { id: 'default', label: 'Default (Matthew, English male fallback)' },
    // Polly has no Yiddish voice; Hebrew often reads Yiddish reasonably.
    { id: 'Suri', label: 'Suri (Hebrew approximation)' },
  ],
};

const MAX_TEXT = 3000;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customerId?: string | null;
  folderId?: string | null;
  onCreated: (row: RecordingRow) => void;
}

export function CreateFromTtsDialog({
  open, onOpenChange, customerId: initialCustomerId, folderId, onCreated,
}: Props) {
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [language, setLanguage] = useState<'en' | 'he' | 'yi'>('en');
  const [voiceId, setVoiceId] = useState<string>('default');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId ?? null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  // Reset the voice selection if the language changes mid-flight — keeping
  // a Hebrew-only voice selected after switching to English would fail on
  // the server.
  useEffect(() => {
    setVoiceId('default');
    // Kill stale preview when switching languages.
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Revoke blob URLs when they're replaced. The AudioPlayer below is bound
  // declaratively to `previewUrl` so React manages src; we just need to free
  // the previous blob when a new preview lands.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const preview = useMutation({
    mutationFn: () => recordings.ttsPreview({
      text: text.trim(),
      language,
      voice_id: voiceId === 'default' ? undefined : voiceId,
    }),
    onSuccess: (blob) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      // Bind declaratively — the AudioPlayer picks up the new src and the
      // user presses Play when ready. Avoids autoplay-policy pitfalls and
      // the imperative setTimeout+src dance that was silently failing.
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'TTS preview failed.';
      if (msg.includes('tts_not_configured')) {
        setServerError('TTS is not configured on this server. Ask your platform admin to set up Amazon Polly credentials.');
      } else {
        setServerError(msg);
      }
    },
  });

  const create = useMutation({
    mutationFn: () => recordings.fromTts({
      name: name.trim(),
      language,
      text: text.trim(),
      voice_id: voiceId === 'default' ? undefined : voiceId,
      ...(folderId ? { folder_id: folderId } : {}),
      ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}),
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['recordings'] });
      onCreated(row);
    },
    onError: (err) => {
      // Surface server 503 "tts_not_configured" as a friendly message.
      const msg = err instanceof Error ? err.message : 'Save failed.';
      if (msg.includes('tts_not_configured')) {
        setServerError('TTS is not configured on this server. Ask your platform admin to set up Amazon Polly credentials.');
      } else {
        setServerError(msg);
      }
    },
  });

  const canPreview = text.trim().length > 0 && !preview.isPending;
  const canSave =
    name.trim().length > 0
    && text.trim().length > 0
    && effectiveCustomerId !== null
    && !create.isPending;

  const isRtl = language === 'he' || language === 'yi';
  const voices = VOICES_BY_LANG[language];

  function handleOpenChange(v: boolean) {
    if (create.isPending) return;
    if (!v) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setName('');
      setText('');
      setPreviewUrl(null);
      setServerError(null);
    }
    onOpenChange(v);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            Create from TTS
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {customerPicker}
          <CustomerScopeChip />

          <div className="space-y-1.5">
            <Label htmlFor="tts-name">Name <span aria-hidden>*</span></Label>
            <Input
              id="tts-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. After-hours greeting"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="tts-lang">Language</Label>
              <Select value={language} onValueChange={(v) => setLanguage(v as typeof language)}>
                <SelectTrigger id="tts-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="he">Hebrew</SelectItem>
                  <SelectItem value="yi">Yiddish</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tts-voice">Voice</Label>
              <Select value={voiceId} onValueChange={setVoiceId}>
                <SelectTrigger id="tts-voice"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {voices.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tts-text">Script <span aria-hidden>*</span></Label>
            <Textarea
              id="tts-text"
              value={text}
              onChange={(e) => {
                const next = e.target.value.slice(0, MAX_TEXT);
                setText(next);
              }}
              rows={5}
              dir={isRtl ? 'rtl' : 'ltr'}
              placeholder={
                isRtl
                  ? 'כתוב את התסריט שיושמע לאחר סינתזה…'
                  : 'Write the text you want synthesized into audio…'
              }
              className="text-sm resize-none"
            />
            <p className="text-[11px] text-slate-400 tabular-nums text-right">
              {text.length.toLocaleString()} / {MAX_TEXT.toLocaleString()} characters
            </p>
          </div>

          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setServerError(null); preview.mutate(); }}
                disabled={!canPreview}
              >
                {preview.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : <Sparkles className="h-4 w-4 mr-1.5" />}
                {preview.isPending ? 'Synthesizing…' : 'Preview'}
              </Button>
              <p className="text-[11px] text-slate-400 ml-auto">
                Preview is free — nothing is saved until you click Save.
              </p>
            </div>
            {previewUrl && (
              <AudioPlayer src={previewUrl} />
            )}
          </div>

          {serverError && (
            <p role="alert" className="text-xs text-red-500">{serverError}</p>
          )}
        </div>

        <DialogFooter className="mt-6">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={!canSave}
            onClick={() => { setServerError(null); create.mutate(); }}
          >
            {create.isPending ? 'Saving…' : 'Save recording'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
